'use client';

import NextLink from 'next/link';
import { useLocale, useTranslations } from 'next-intl';

import { MaterialDesignIcon } from '@hrworks/sui-core/MaterialDesignIcon';
import { Tooltip } from '@hrworks/sui-core/Tooltip';

import { useIsCustomDomain } from '../../context/CustomDomainContext';
import { resolveRoute } from '../../util/routeResolver';
import { CardBody, CardHeader } from '../Card';
import { HTML } from '../Html';
import { Title } from '../Title';
import { S } from './JobPost.styles';
import type { JobPostProps } from './JobPost.types';
import { Tags } from './Tags';

const MAX_VISIBLE_CITIES = 3;

export const JobPost = ({ post, postOffer, companyId, remoteWorkName, ...otherProps }: JobPostProps) => {
  const t = useTranslations('Job');
  const locale = useLocale();
  const isCustom = useIsCustomDomain();
  const previewDescription = post.previewDescription ?? '';
  const {
    id,
    displayName,
    careerLevelKey,
    workingTimeModelKey,
    employmentContractKey,
    permanentEstablishments,
    placeOfWorkTypeKey,
  } = postOffer;

  const placeOfWorkLabel =
    placeOfWorkTypeKey &&
    placeOfWorkTypeKey !== 'onsite' &&
    (placeOfWorkTypeKey === 'remote' && remoteWorkName === 'workRemotely'
      ? t('remoteWorkNameKey.workRemotely')
      : t(`placeOfWorkTypeKey.${placeOfWorkTypeKey}`));

  const tagList = [
    careerLevelKey && careerLevelKey !== 'notSpecified' && t(`careerLevelKey.${careerLevelKey}`),
    workingTimeModelKey && workingTimeModelKey !== 'notSpecified' && t(`workingTimeModelKey.${workingTimeModelKey}`),
    employmentContractKey &&
      employmentContractKey !== 'notSpecified' &&
      t(`employmentContractTypeKey.${employmentContractKey}`),
    placeOfWorkLabel,
  ].filter((tag): tag is string => Boolean(tag));

  const uniqueCities = [...new Set(permanentEstablishments?.map((e) => e?.address?.city).filter(Boolean))];
  const visibleCities = uniqueCities.slice(0, MAX_VISIBLE_CITIES);
  const remainingCities = uniqueCities.slice(MAX_VISIBLE_CITIES);

  return (
    <S.Card
      data-guide-id="joblist-card"
      component={NextLink}
      // Kein Prefetch, siehe components/Link/Link.tsx.
      prefetch={false}
      href={
        id
          ? resolveRoute('jobPost', { locale, companyId: isCustom ? undefined : companyId, id })
          : resolveRoute('jobList', { locale, companyId: isCustom ? undefined : companyId })
      }
      {...otherProps}
    >
      <CardHeader>
        <Title data-guide-id="joblist-card-title" typography="headingLg" component="h2">
          {displayName}
        </Title>
      </CardHeader>
      <CardBody>
        <S.Content>
          <S.Main>
            <S.Description data-guide-id="joblist-card-description">
              <HTML html={previewDescription} />
            </S.Description>
            <Tags data-guide-id="joblist-card-tags" tags={tagList} />
          </S.Main>
          <S.Sidebar>
            {visibleCities.map((city, index) => (
              <S.SidebarElement key={city} data-guide-id="joblist-card-location">
                <MaterialDesignIcon name="location_on" />
                <S.SidebarElementText>{city}</S.SidebarElementText>
                {index === visibleCities.length - 1 && remainingCities.length > 0 && (
                  <Tooltip text={remainingCities.join(', ')} placement="top" trigger="hoverOrTouch">
                    <S.MoreBadge
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                      }}
                    >
                      +{remainingCities.length}
                    </S.MoreBadge>
                  </Tooltip>
                )}
              </S.SidebarElement>
            ))}
          </S.Sidebar>
        </S.Content>
      </CardBody>
    </S.Card>
  );
};
